import moment from 'moment';
import React, {
    useCallback, useEffect, useMemo, useState, FC,
} from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { date, object, SchemaOf } from 'yup';
import {
    Button,
    Icon,
    Loader,
    Modal, notification, NumberInput, DatePicker,
} from 'ui-library';
import { BUTTON_TYPE_PRIMARY } from 'ui-library/components/Button/constants';
import { ICON_TYPE_DELETE, ICON_TYPE_CLOSE_SLIM } from 'ui-library/components/Icon';
import ServiceManager from 'services/ServiceManager';
import { useSinglePortfolio } from 'pages/ClientOverview/pages/Portfolios/pages/SinglePortfolioView/context';
import { useYupValidationResolver } from '../../../../../../../../hooks/useYupValidationResolver';

import './AdministrationFeeModal.css';

import {
    addDaysToDate,
    compareDates, dateFormatZurich, formatDateWithoutLocalConversion, isQuarterEndDate,
} from '../../../../../../../../utils/datetime';
import { FEE_TYPES } from 'datasource/CreatePortfolio/constants';

// --- Interfaces ---

interface FeeData {
    fee?: number | string;
    discountValue?: number;
    startDate?: moment.Moment | Date | string;
    expiryDate?: moment.Moment | Date | string | null;
}

interface AdministrationFeeModalProps {
    handleClose: () => void;
    showModal: boolean;
    feeData: FeeData | null | undefined;
    onSave: (feeType: number, data: any) => Promise<void>;
    defaultAdministrationFee: number;
    onDeleteFee?: (data: {
        fee: number;
        feeType: number;
        startDate: moment.Moment;
        discountValue: null;
    }) => void;
    showDeleteButton?: boolean;
}

interface FormValues {
    startDate: moment.Moment | null;
    expiryDate: moment.Moment | null;
}

// --- Component ---

const AdministrationFeeModal: FC<AdministrationFeeModalProps> = ({
    handleClose,
    showModal,
    feeData,
    onSave,
    defaultAdministrationFee,
    onDeleteFee,
    showDeleteButton = false,
}) => {
    const { t } = useTranslation();
    const [amFee, setAssetFee] = useState<number | string>(feeData?.fee ?? defaultAdministrationFee);
    const [discountValue, setDiscountValue] = useState<number>(feeData?.discountValue || 0);
    const [inProgress, setInProgress] = useState<boolean>(false);

    const { investmentAppId } = useSinglePortfolio();

    useEffect(() => {
        if (!discountValue) {
            setDiscountValue(feeData?.discountValue || 0);
        }
    }, [feeData?.discountValue]);

    const schema: SchemaOf<FormValues> = useMemo(() => object().shape({
        startDate: date().required(t('validation.mandatoryField')).nullable(),
        expiryDate: date().nullable(),
    }) as any, [t]);

    const resolver = useYupValidationResolver(schema);

    const defaultValues: FormValues = useMemo(() => ({
        startDate: feeData?.startDate ? moment.utc(feeData.startDate) : moment.utc(),
        expiryDate: feeData?.expiryDate ? moment.utc(feeData.expiryDate) : null,
    }), [feeData]);

    const {
        handleSubmit, formState: { errors, isDirty }, control, watch, reset,
    } = useForm<FormValues>({
        resolver,
        defaultValues,
    });

    const formData = watch();

    useEffect(() => {
        reset(defaultValues);
        setAssetFee(feeData?.fee ?? defaultAdministrationFee);
    }, [feeData, reset, defaultValues, defaultAdministrationFee]);

    const onClose = () => {
        reset(defaultValues);
        setAssetFee(feeData?.fee ?? defaultAdministrationFee);
        setDiscountValue(feeData?.discountValue || 0);
        handleClose();
    };

    const saveAssetChanges = async () => {
        try {
            setInProgress(true);
            const finalData = {
                fee: typeof amFee === 'string' ? parseFloat(amFee) : amFee,
                discountValue,
                startDate: formatDateWithoutLocalConversion(formData?.startDate, (moment as any).defaultFormatUtc),
                expiryDate: formData?.expiryDate ? formatDateWithoutLocalConversion(formData?.expiryDate, (moment as any).defaultFormatUtc) : null,
                feeType: FEE_TYPES.administration,
            };

            await onSave(FEE_TYPES.administration as number, finalData);
            handleClose();
        } finally {
            setInProgress(false);
        }
    };

    const onDelete = useCallback(async () => {
        try {
            setInProgress(true);
            await ServiceManager.customInvestmentService('deleteFee', [{
                investmentApplicationId: investmentAppId,
                feeType: FEE_TYPES.administration,
            }]);

            if (onDeleteFee) {
                onDeleteFee({
                    fee: defaultAdministrationFee,
                    feeType: FEE_TYPES.administration as number,
                    startDate: moment(),
                    discountValue: null,
                });
            }

            onClose();
            notification.open({ content: t('feeModal.feeDeletedSuccessfully'), type: 'success' });
        } catch (err) {
            notification.open({ content: t('general.somethingWentWrong'), type: 'error' });
        } finally {
            setInProgress(false);
        }
    }, [onDeleteFee, investmentAppId, defaultAdministrationFee, t]);

    const confirmDeletion = useCallback(() => {
        Modal.confirm({
            title: t('feeModal.deleteDiscountConfirmation.title'),
            content: t('feeModal.deleteDiscountConfirmation.content'),
            onOk: onDelete,
        });
    }, [onDelete, t]);

    const renderFooter = useMemo(() => (
        <div className="AssetFee__ButtonWrapper">
            <Button
                disabled={inProgress}
                onClick={confirmDeletion}
                type="danger"
                className={(!feeData?.discountValue || !showDeleteButton) ? 'hidden' : ''}
            >
                <Icon type={ICON_TYPE_DELETE} />
                {t('custodyFeeModal.buttons.deleteDiscount')}
            </Button>
            <Button disabled={inProgress} onClick={onClose}>
                {t('custodyFeeModal.buttons.cancel')}
            </Button>
            <Button
                type={BUTTON_TYPE_PRIMARY}
                disabled={(!isDirty && !discountValue) || inProgress}
                onClick={handleSubmit(() => saveAssetChanges())}
            >
                {inProgress ? <Loader /> : t('custodyFeeModal.buttons.save')}
            </Button>
        </div>
    ), [inProgress, confirmDeletion, feeData?.discountValue, showDeleteButton, isDirty, discountValue, handleSubmit, saveAssetChanges, onClose, t]);

    const handleDiscountChange = (e: any) => {
        const val = typeof e === 'object' ? e.target.value : e;
        const numericVal = parseFloat(val) || 0;
        const tempCustodyFee = Math.max(defaultAdministrationFee * (1 - numericVal / 100), 0);

        setAssetFee(tempCustodyFee?.toFixed(3).replace(/\.?0*$/, ''));
        setDiscountValue(numericVal);
    };

    const closeIcon = useMemo(() => (<Icon type={ICON_TYPE_CLOSE_SLIM} size={20} />), []);

    return (
        <Modal
            title={t('AdministrationFeeModal.labels.title')}
            width={740}
            visible={showModal}
            onCancel={onClose}
            footer={renderFooter}
            keyboard={false}
            maskClosable={false}
            closable
            borderRadius
            closeIcon={closeIcon}
        >
            <form onSubmit={(e) => e.preventDefault()}>
                {feeData?.fee && (
                    <div className="AssetFee__FieldWrapper">
                        <p className="fee-label">{t('AdministrationFeeModal.labels.previousAdministrationFee')}</p>
                        <p>
                            {feeData?.fee}
                            %
                        </p>
                    </div>
                )}

                <div className="AssetFee__FieldWrapper">
                    <p className="fee-label">{t('AdministrationFeeModal.labels.regularFee')}</p>
                    <p>
                        {defaultAdministrationFee}
                        %
                    </p>
                </div>
                <div className="AssetFee__FieldWrapper">
                    <p className="fee-label">{t('AdministrationFeeModal.labels.percentageFee')}</p>
                    <NumberInput
                        className="AssetFee__Discount"
                        value={discountValue}
                        validation={{
                            maxValue: 100,
                            minValue: 0,
                        }}
                        suffix="%"
                        onChange={handleDiscountChange}
                    />
                </div>
                <div className="AssetFee__FieldWrapper">
                    <p className="fee-label">{t('AdministrationFeeModal.labels.startDate')}</p>
                    <Controller
                        control={control}
                        name="startDate"
                        render={({ field }) => (
                            <DatePicker
                                {...field}
                                value={formData?.startDate && formatDateWithoutLocalConversion(formData?.startDate, dateFormatZurich)}
                                format={dateFormatZurich}
                                data-cy="startDate"
                                error={errors?.startDate}
                                disabledDate={(current) => !!(current && (compareDates(current, formData.expiryDate) || moment(current) < moment(addDaysToDate(new Date(), -1))))}
                                required
                            />
                        )}
                    />
                </div>
                <div className="AssetFee__FieldWrapper">
                    <div className="AssetFee__LabelWrapper fee-label">
                        <p>{t('AdministrationFeeModal.labels.expiryDate')}</p>
                        <span>{t('custodyFeeModal.labels.quarterDatesInfo')}</span>
                    </div>
                    <Controller
                        control={control}
                        name="expiryDate"
                        render={({ field }) => (
                            <DatePicker
                                {...field}
                                value={formData?.expiryDate && formatDateWithoutLocalConversion(formData?.expiryDate, dateFormatZurich)}
                                format={dateFormatZurich}
                                data-cy="expiryDate"
                                errors={errors?.expiryDate}
                                disabledDate={(current) => !!(current && (compareDates(formData.startDate, current) || isQuarterEndDate(current)))}
                                required
                            />
                        )}
                    />
                </div>
                <div className="AssetFee__FieldWrapper">
                    <p className="fee-label">{t('AdministrationFeeModal.labels.newFee')}</p>
                    <p>
                        <b>
                            {amFee}
                            %
                        </b>
                    </p>
                </div>
            </form>
        </Modal>
    );
};

export default AdministrationFeeModal;
